import React, { FC, useContext } from 'react';
import styled from 'styled-components';
import dayjs from 'dayjs';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

import { RouterLink, SelectedIndexContext } from '../../App';
import { CentreHeading, FlexRowCentred, FlexRowSpaceAround } from '../common-styles/common-styles';
import { useFetchItinerary } from '../../api/hooks/use-fetch-itinerary';
import { Itinerary, ItineraryDay, ItineraryItem } from '../../../database.schema';
import { HamburgerBar1, HamburgerBar2, HamburgerContainer } from '../navigation/navigation-styles';

const getInitialSlide = (itinerayDays: Array<ItineraryDay>) => {
  const index = itinerayDays.findIndex((day: ItineraryDay) => day.date === dayjs().format('YYYY-MM-DD'));
  if (index !== -1) {
    return index;
  }
  return 0;
};

const getDate = (date: string) => {
  const dateIn = dayjs(date);
  if (dayjs().add(1, 'day').isSame(dayjs(date), 'day')) {
    return `Tomorrow (${dateIn.format('D')}th)`;
  }
  if (dayjs().subtract(1, 'day').isSame(dayjs(date), 'day')) {
    return `Yesterday (${dateIn.format('D')}th)`;
  }
  if (dayjs().isSame(dayjs(date), 'day')) {
    return `Today (${dateIn.format('D')}th)`;
  }
  return `${dateIn.format('dddd')} (${dateIn.format('D')}th)`;
};

const TableWrapper = styled.div<{ colour?: string }>`
  width: 100%;
  margin-bottom: 0.5rem;
  padding: 0 0.25rem;
  table {
    text-align: left;
    min-height: 6rem;
    width: 100%;
    border-spacing: 0;
    th,
    td {
      padding-left: 0.5rem;
      background: ${(props) => props.colour || 'black'};
      color: white;
      h3 {
        text-align: left;
      }
    }
  }
  table tr:first-child td:first-child {
    border: 2px solid;
    border-color: ${(props) => props.colour || 'black'};
    border-top-left-radius: 10px;
  }

  table tr:first-child td:last-child {
    border: 2px solid;
    border-color: ${(props) => props.colour || 'black'};
    border-top-right-radius: 10px;
    width: 6rem;
    text-align: center;
  }

  table tr:last-child td:last-child {
    border: 2px solid;
    border-color: ${(props) => props.colour || 'black'};
    border-bottom-right-radius: 10px;
    width: 6rem;
    text-align: center;
  }
  table tr:last-child td:first-child {
    border: 2px solid;
    border-color: ${(props) => props.colour || 'black'};
    border-bottom-left-radius: 10px;
  }
`;

const Time = styled.span`
  font-size: 1.5rem;
  font-weight: bold;
`;
const ItineraryWrapper = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  text-align: left;
  width: 100vw;
  height: 100vh;
  background: white;
  z-index: 9999999;
`;

const ItinerayDay = styled.div`
  font-size: 1.75rem;
  color: red;
  margin-bottom: 1rem;
`;

const ItineraryHeadingWrapper = styled.div`
  display: flex;
  justify-content: left;
`;

export const ItineraryPage: FC = () => {
  const { selectedTournamentIndex } = useContext(SelectedIndexContext);
  const { data, error, isLoading } = useFetchItinerary(selectedTournamentIndex);

  if (isLoading) {
    return <div>Loading...</div>;
  }
  if (error) {
    console.log('<><Itinerary Error><', error);
    return <div>Error</div>;
  }
  const itinerary = data as Itinerary;
  const settings = {
    dots: false,
    infinite: false,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    initialSlide: getInitialSlide(itinerary.days),
  };

  if (itinerary && itinerary.itemColours) {
    return (
      <ItineraryWrapper>
        <CentreHeading style={{ height: '100vh', overflow: 'scroll' }}>
          <div style={{ position: 'relative' }}>
            <h2 style={{ display: 'inline-block', height: '3rem', padding: '0.5rem 3rem 0.5rem 0', marginBottom: '0.5rem', borderBottom: '3px dashed black' }}>
              Golf Tour 2026 Itinerary
            </h2>
            <RouterLink to={'/'}>
              <HamburgerContainer>
                <FlexRowCentred>
                  <div>
                    <HamburgerBar1 data-testid="bar1" style={{ transform: 'translate(2px, 8px) rotate(-45deg)', backgroundColor: 'black' }}></HamburgerBar1>
                    <HamburgerBar2 data-testid="bar2" style={{ opacity: 0 }}></HamburgerBar2>
                    <HamburgerBar1 data-testid="bar3" style={{ transform: 'translate(2px, -11px) rotate(45deg)', backgroundColor: 'black' }}></HamburgerBar1>
                  </div>
                </FlexRowCentred>
              </HamburgerContainer>
            </RouterLink>
          </div>

          <Slider {...settings}>
            {itinerary.days.map((day: ItineraryDay) => (
              <div key={day.date}>
                <ItinerayDay>{getDate(day.date)}</ItinerayDay>
                {day.items.map((item: ItineraryItem, index: number) => (
                  <TableWrapper key={`${day.date}-item-${index}`} colour={itinerary.itemColours[`${item.type}`]}>
                    <table>
                      <tbody>
                        <tr>
                          <td>
                            <ItineraryHeadingWrapper>
                              <img style={{ height: '30px', width: '30px', marginRight: '0.5rem' }} src={item.icon} />
                              <h3>{item.heading}</h3>
                            </ItineraryHeadingWrapper>
                          </td>
                          <td>
                            <div>{item.timeCopy}</div>
                          </td>
                        </tr>
                        <tr>
                          <td>
                            {item.content.map((content: string, index: number) => (
                              <div key={`${item.type}-${content}-${index}`}>{content}</div>
                            ))}
                          </td>
                          <td>
                            <Time>{item.time}</Time>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </TableWrapper>
                ))}
              </div>
            ))}
          </Slider>
        </CentreHeading>
      </ItineraryWrapper>
    );
  }
  return (
    <ItineraryWrapper>
      <CentreHeading style={{ height: '100vh', overflow: 'scroll' }}>No Itinerary</CentreHeading>
      <RouterLink to={'/'}>
        <HamburgerContainer>
          <FlexRowCentred>
            <div>
              <HamburgerBar1 data-testid="bar1" style={{ transform: 'translate(2px, 8px) rotate(-45deg)', backgroundColor: 'black' }}></HamburgerBar1>
              <HamburgerBar2 data-testid="bar2" style={{ opacity: 0 }}></HamburgerBar2>
              <HamburgerBar1 data-testid="bar3" style={{ transform: 'translate(2px, -11px) rotate(45deg)', backgroundColor: 'black' }}></HamburgerBar1>
            </div>
          </FlexRowCentred>
        </HamburgerContainer>
      </RouterLink>
    </ItineraryWrapper>
  );
};
